// @ts-strict-ignore
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { ArticleVO } from '@article/model/vo/article.vo';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProjectModel } from '@project/model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { CustomValidators } from '@shared/custom-validators';
import { ArticleUtils } from '@article/util/article-utils';
import { AlertButton } from '@shared/alert/alertButton';
import { AdmintoolProjectService } from '../../project/admintool-project.service';
import { ImportArticleService } from './import-article.service';

export enum ImportArticleSource {
  WX_DATABASE = 'wxDatabase',
  MODEL_FILE = 'modelFile'
}

export enum ImportArticleType {
  CREATE_NEW_ARTICLE = 'createNewArticle',
  REPLACE_MODEL = 'replaceModel'
}

@Component({
  standalone: false,
  selector: 'app-import-article-modal',
  templateUrl: './import-article.component.html'
})
export class ImportArticleComponent implements OnInit {

  public readonly ImportArticleSource = ImportArticleSource;
  public readonly importArticleSources: string[] = [ImportArticleSource.WX_DATABASE, ImportArticleSource.MODEL_FILE];
  public readonly importArticleTypes: string[] = [ImportArticleType.CREATE_NEW_ARTICLE, ImportArticleType.REPLACE_MODEL];

  @Input() public articleVO: ArticleVO;
  @Output() public finish = new EventEmitter<void>();

  public form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private bsModalRef: BsModalRef,
    private projectModel: ProjectModel,
    private alertHelper: AlertHelper,
    private localeResourceManager: LocaleResourceManager,
    private admintoolProjectService: AdmintoolProjectService,
    private importArticleService: ImportArticleService,
  ) {
  }

  ngOnInit() {
    this.form = this.fb.group({
      source: [ImportArticleSource.WX_DATABASE],
      articleIdOrNumber: [this.articleVO ? this.articleVO.articleNo : '', this.validateArticleIdOrNumberInputRequired()],
      importType: [ImportArticleType.CREATE_NEW_ARTICLE]
    });
    this.form.get('source').valueChanges.subscribe(() => {
      this.form.get('articleIdOrNumber').markAsUntouched();
      this.form.get('articleIdOrNumber').updateValueAndValidity();
    });
  }

  private validateArticleIdOrNumberInputRequired(): ValidatorFn {
    return (c: FormControl) => {
      const selectedSource: ImportArticleSource = this.form?.get('source').value;
      return selectedSource === ImportArticleSource.WX_DATABASE ? CustomValidators.trimRequired(c) : null;
    };
  }

  public getInputError(c: AbstractControl): string {
    if (c && c.hasError('messageKey')) {
      return this.localeResourceManager.getValueByKey(c.getError('messageKey'), { 0: c.getError('param') });
    } else {
      return '';
    }
  }

  public isImportArticleTypeDisabled(importType: string): boolean {
    if (importType === ImportArticleType.REPLACE_MODEL) {
      return !this.articleVO || ArticleUtils.isCompositeArticle(this.articleVO);
    }
    return false;
  }

  public close(): void {
    this.bsModalRef.hide();
    this.finish.emit();
  }

  public async importArticle(): Promise<void> {
    const importType: ImportArticleType = this.form.get('importType').value;
    if (importType === ImportArticleType.CREATE_NEW_ARTICLE) {
      if (await this.checkUnsavedChanges()) {
        await this.createNewArticle();
      }
    }
    if (importType === ImportArticleType.REPLACE_MODEL) {
      await this.replaceModel();
    }
  }

  private async checkUnsavedChanges(): Promise<boolean> {
    if (this.projectModel.isCurrentProjectChanged) {
      const answer = await this.alertHelper.showAlert(
        'admintool.importArticle.alert.unsavedChanges.title',
        'admintool.importArticle.alert.unsavedChanges.message',
        null, [AlertButton.CANCEL, AlertButton.NEXT], '', {nextLabel: 'global.label.continue'});
      return answer === AlertButton.NEXT;
    }
    return true;
  }

  private async createNewArticle(): Promise<void> {
    const source: ImportArticleSource = this.form.get('source').value;
    if (source === ImportArticleSource.WX_DATABASE) {
      const success = await this.importArticleFromWxDatabase();
      if (success) {
        this.close();
      }
    }
    if (source === ImportArticleSource.MODEL_FILE) {
      const success = await this.importArticleFromFile();
      if (success) {
        this.close();
      } else {
        await this.admintoolProjectService.clearCurrentArticle();
      }
    }
  }

  private async replaceModel(): Promise<void> {
    const source: ImportArticleSource = this.form.get('source').value;
    if (source === ImportArticleSource.WX_DATABASE) {
      await this.replaceArticleFromWxDatabase();
      this.close();
    }
    if (source === ImportArticleSource.MODEL_FILE) {
      await this.replaceArticleFromModelFile();
      this.close();
    }
  }

  private async importArticleFromWxDatabase(): Promise<boolean> {
    const articleIdOrNumber: string = this.form.get('articleIdOrNumber').value;
    const success = await this.importArticleService.importArticleFromWxDatabase(articleIdOrNumber);
    return success;
  }

  private async replaceArticleFromWxDatabase(): Promise<boolean> {
    const articleIdOrNumber: string = this.form.get('articleIdOrNumber').value;
    const success = await this.importArticleService.replaceArticleFromWxDatabase(this.articleVO, articleIdOrNumber);
    return success;
  }

  private async importArticleFromFile(): Promise<boolean> {
    const success = await this.importArticleService.importArticleFromFile();
    return success;
  }

  private async replaceArticleFromModelFile(): Promise<boolean> {
    const success = await this.importArticleService.replaceArticleFromFile(this.articleVO);
    return success;
  }
}
